import {
  getMessageFromNetworkError,
  getPrivateFolderOwnerMismatch,
  useMoveAssetsToFolder,
  type PrivateFolderOwnerMismatch,
  type PrivateFolderOwnerPolicy,
} from "@zapier/client";
import { getFolderUrl, useNotification } from "@zapier/utils";
import { Link, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";

interface Input {
  assetTitle: string | undefined | null;
  assetParentId?: string | null;
  onSettled?: () => void;
  isBulk?: boolean;
}

interface MoveOptions {
  assetIds: string[];
  selectedFolderId: string;
  selectedFolderName: string;
  privateFolderOwnerPolicy?: PrivateFolderOwnerPolicy;
  /** Called instead of the error toast when the move was refused under the "refuse" policy. */
  onPrivateFolderOwnerMismatch?: (mismatch: PrivateFolderOwnerMismatch) => void;
  /** Called once the move succeeded or failed for real, not when it was refused. */
  onSettled?: () => void;
}

export function useMoveAssetActions({
  assetTitle,
  assetParentId,
  onSettled,
  isBulk = false,
}: Input) {
  const t = useTranslations("move-asset-actions");
  const { createNotification } = useNotification();
  const { moveAssetsToFolder, isPending } = useMoveAssetsToFolder({
    folderId: assetParentId,
  });

  // Move assets between folders
  const moveAssetsToFolderWithToasts = ({
    assetIds,
    selectedFolderId,
    selectedFolderName,
    privateFolderOwnerPolicy,
    onPrivateFolderOwnerMismatch,
    onSettled: onMoveSettled,
  }: MoveOptions) => {
    const settle = () => {
      onMoveSettled?.();
      onSettled?.();
    };

    moveAssetsToFolder(
      {
        destinationFolderId: selectedFolderId,
        assetIds,
        ...(privateFolderOwnerPolicy ? { privateFolderOwnerPolicy } : {}),
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.move-success", {
                  assetTitle: assetTitle || "",
                  isBulk: String(isBulk),
                  selectedFolderName,
                  folderLink: (chunks) => (
                    <Link href={getFolderUrl(selectedFolderId)}>{chunks}</Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
          settle();
        },
        onError: (error: any) => {
          const mismatch = getPrivateFolderOwnerMismatch(error);
          if (mismatch && onPrivateFolderOwnerMismatch) {
            onPrivateFolderOwnerMismatch(mismatch);
            return;
          }
          createNotification({
            title: t("errors.unable-to-move", { assetTitle: assetTitle || "" }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
          settle();
        },
      },
    );
  };

  return {
    moveAssetsToFolderWithToasts,
    isPending,
  };
}
